import { ref, computed } from '@vue/composition-api';
import i18n from '@/i18n';
import vuetify from '@/plugins/vuetify';
import httpClient from '@/api';
import mediaServerHttpClient from '@/mediaServerClient';
import { localeType } from '@/enums';

const globalLocale = ref(null);

export default function useLocale() {
  function changeLocale(locale) {
    globalLocale.value = locale;
    i18n.locale = locale;
    vuetify.framework.lang.current = locale;
    httpClient.defaults.headers.common['Accept-Language'] = locale;
    mediaServerHttpClient.defaults.headers.common['Accept-Language'] = locale;
  }

  function saveLocale(locale) {
    if (process.browser) {
      localStorage.setItem('locale', locale);
    }
  }

  function saveLocaleIfEmptyStorageValue(locale) {
    if (process.browser) {
      if (!localStorage.getItem('locale')) {
        saveLocale(locale);
      }
    }
  }

  function getPreferredLocale() {
    if (process.server) {
      return null;
    }

    return localStorage.getItem('locale');
  }

  function detectBrowserLocale() {
    if (process.server) {
      return localeType.EN;
    }

    const browserLocale = window.navigator.language || window.navigator.userLanguage;

    if (!browserLocale) {
      return localeType.DEFAULT;
    }

    const locale = browserLocale.split('-').shift();

    if (!locale || !Object.keys(localeType).includes(locale.toUpperCase())) {
      return localeType.DEFAULT;
    }

    return localeType[locale.toUpperCase()];
  }

  function detectLocale() {
    if (process.server) {
      return localeType.EN;
    }

    return localStorage.getItem('locale') ?? detectBrowserLocale();
  }

  function setLocaleFromQuery(route, fallbackLocale = null) {
    const localeFromQuery = route?.query?.locale;
    if (localeFromQuery) {
      changeLocale(localeFromQuery);
      saveLocaleIfEmptyStorageValue(localeFromQuery);
      return;
    }

    const locale = getPreferredLocale() || fallbackLocale;
    if (locale) {
      changeLocale(locale);
      saveLocaleIfEmptyStorageValue(locale);
    }
  }

  return {
    locale: computed(() => globalLocale.value),
    changeLocale,
    saveLocale,
    getPreferredLocale,
    detectLocale,
    saveLocaleIfEmptyStorageValue,
    setLocaleFromQuery,
  };
}
